import React, { useState, useImperativeHandle, forwardRef, useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useEditor, EditorContent, ReactNodeViewRenderer } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Highlight } from '@tiptap/extension-highlight';
import Link from '@tiptap/extension-link';
import Code from '@tiptap/extension-code';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableHeader } from '@tiptap/extension-table-header';
import { TableCell } from '@tiptap/extension-table-cell';
import { COLOR_SYSTEM } from '../../design/design-system';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon';
import { getDocumentTypeFromRefId, DocumentType } from '../../../utils/documentTypeUtils';
import { Reference } from './ReferenceNode';
import { Hospital } from './HospitalNode';
import { DrugImage } from './DrugImageNode';
import { Drug, DrugSearchResult } from '../../../common/models/types';

const EditorWrapper = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: white;
`;

const ToolbarArea = styled.div`
  flex: 0 0 auto;
  background: white;
`;

const EditorToolbar = styled.div`
  display: flex;
  padding: 8px;
  background: white;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border: 1px solid ${COLOR_SYSTEM.get('Gray')[200]};
  margin-bottom: 16px;
  position: relative;
`;

const ToolbarLeft = styled.div`
  display: flex;
  gap: 1px;
  align-items: center;
  overflow-x: auto;
  flex: 1;
  margin-right: 12px;
  
  /* 스크롤바 숨기기 */
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* Firefox */
  scrollbar-width: none;
  
  /* IE and Edge */
  -ms-overflow-style: none;
`;

const ToolbarGroup = styled.div`
  display: flex;
  gap: 1px;
  margin: 0 6px;
  
  &:first-child {
    margin-left: 0;
  }
  
  &:last-child {
    margin-right: 0;
  }
`;

const ToolbarDivider = styled.div`
  width: 1px;
  height: 20px;
  background: ${COLOR_SYSTEM.get('Gray')[300]};
  margin: 0 8px;
  flex-shrink: 0;
`;

const ScrollIndicatorLeft = styled.div<{ visible: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  background: linear-gradient(to right, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  z-index: 1;
  border-radius: 8px 0 0 8px;
`;

const ScrollIndicatorRight = styled.div<{ visible: boolean }>`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 40px;
  background: linear-gradient(to left, rgba(255, 255, 255, 1), rgba(255, 255, 255, 0));
  pointer-events: none;
  opacity: ${props => props.visible ? 1 : 0};
  transition: opacity 0.3s ease;
  z-index: 1;
  border-radius: 0 8px 8px 0;
`;

const ScrollArrowLeft = styled.button<{ visible: boolean }>`
  position: absolute;
  left: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid ${COLOR_SYSTEM.get('Gray')[300]};
  background: white;
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${COLOR_SYSTEM.get('Gray')[50]};
  }
`;

const ScrollArrowRight = styled.button<{ visible: boolean }>`
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  width: 24px;
  height: 24px;
  border-radius: 4px;
  border: 1px solid ${COLOR_SYSTEM.get('Gray')[300]};
  background: white;
  display: ${props => props.visible ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  
  &:hover {
    background: ${COLOR_SYSTEM.get('Gray')[50]};
  }
`;

const ColorDropdown = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid ${COLOR_SYSTEM.get('Gray')[300]};
  border-radius: 8px;
  background: white;
`;

const ColorPickerButton = styled.button`
  width: 16px;
  height: 24px;
  border: none;
  border-radius: 0 3px 3px 0;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;

  &:hover:not(:disabled) {
    background: ${COLOR_SYSTEM.get('Gray')[100]};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  &::after {
    content: '▼';
    font-size: 8px;
    color: ${COLOR_SYSTEM.get('Gray')[600]};
  }
`;

const ColorGrid = styled.div`
  position: fixed;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get('Gray')[300]};
  border-radius: 4px;
  padding: 8px;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  gap: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  margin-top: 4px;
`;

const ColorButton = styled.button<{ color: string }>`
  width: 24px;
  height: 24px;
  border: 1px solid ${COLOR_SYSTEM.get('Gray')[300]};
  border-radius: 2px;
  background-color: ${props => props.color};
  cursor: pointer;
  transition: transform 0.1s;

  &:hover {
    transform: scale(1.1);
  }
`;

const TableDropdown = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 2px;
  border: 1px solid ${COLOR_SYSTEM.get('Gray')[300]};
  border-radius: 8px;
  background: white;
`;

const TableMenuGrid = styled.div`
  position: fixed;
  background: white;
  border: 1px solid ${COLOR_SYSTEM.get('Gray')[300]};
  border-radius: 4px;
  padding: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
  display: flex;
  gap: 2px;
`;

const TableMenuItem = styled.button`
  padding: 6px 10px;
  border: none;
  background: white;
  cursor: pointer;
  font-size: 12px;
  color: ${COLOR_SYSTEM.get('Gray')[700]};
  white-space: nowrap;
  border-radius: 3px;

  &:hover:not(:disabled) {
    background: ${COLOR_SYSTEM.get('Gray')[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    color: ${COLOR_SYSTEM.get('Gray')[400]};
  }
`;

const ToolbarButton = styled.button<{ isActive?: boolean; colorPreview?: string; isColorButton?: boolean }>`
  padding: 4px 8px;
  border: none;
  border-radius: ${props => props.isColorButton ? '3px 0 0 3px' : '3px'};
  background: ${props => props.isActive ? COLOR_SYSTEM.get('Gray')[200] : 'transparent'};
  color: ${props => props.isActive ? COLOR_SYSTEM.get('Gray')[900] : COLOR_SYSTEM.get('Gray')[700]};
  cursor: pointer;
  font-size: 13px;
  transition: all 0.1s;
  position: relative;
  white-space: nowrap;
  min-width: 24px;
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: ${props => props.isActive ? COLOR_SYSTEM.get('Gray')[300] : COLOR_SYSTEM.get('Gray')[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => props.colorPreview && `
    &::after {
      content: '';
      position: absolute;
      bottom: 2px;
      left: 50%;
      transform: translateX(-50%);
      width: 14px;
      height: 2px;
      background-color: ${props.colorPreview};
      border-radius: 1px;
    }
  `}
`;

const EditArea = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
`;

const EditorContentWrapper = styled.div`
  min-height: 100%;
  
  /* Text Body1과 동일한 기본 폰트 스타일 */
  font-family: 'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, sans-serif;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: -0.02em;
  font-weight: 400;
  color: ${COLOR_SYSTEM.get('Gray')[800]};


  .ProseMirror {
    padding: 0;
    margin: 0;
    border: none;
    outline: none;
    cursor: text;
    min-height: 100%;
    box-sizing: border-box;

    &:empty::before {
      content: attr(data-placeholder);
      color: ${COLOR_SYSTEM.get('Gray')[400]};
      pointer-events: none;
      position: absolute;
    }

    /* Badge 스타일 - mark 태그의 padding과 border-radius 보장 */
    mark[data-color="#1a1a1a"] {
      padding: 4px 14px !important;
      border-radius: 15px !important;
      display: inline-block !important;
      margin: 12px 0 8px 0 !important;
      background-color: #1a1a1a !important;
      color: #ffffff !important;
      font-size: 14px !important; /* 14px로 조정 */
      font-weight: 600 !important;
      line-height: 1.2 !important;

      /* 내부 span의 스타일도 보장 */
      span {
        color: #ffffff !important;
        font-size: 14px !important;
        font-weight: 600 !important;
        line-height: 1.2 !important;
      }
    }

    /* ReviewSector ReviewSectionBody와 동일한 스타일링 적용 */
    h1 {
      font-size: 24px;
      font-weight: 700;
      line-height: 1.3;
      margin: 20px 0 12px 0;
      word-wrap: break-word;
      word-break: break-word;
    }
    
    h2 {
      font-size: 20px;
      font-weight: 600;
      line-height: 1.3;
      margin: 16px 0 10px 0;
      word-wrap: break-word;
      word-break: break-word;
    }
    
    h3 {
      font-size: 16px;
      font-weight: 600;
      line-height: 1.3;
      margin: 12px 0 8px 0;
      word-wrap: break-word;
      word-break: break-word;
    }
    
    /* 기본 텍스트는 Text Body1 스타일 적용 */
    p, li, td, span { 
      margin: 8px 0;
    }
    
    ul, ol { 
      margin: 8px 0; 
      padding-left: 20px;
    }
    
    li { 
      margin: 4px 0;
    }
    
    blockquote {
      background: ${COLOR_SYSTEM.get("Gray")[10]};
      border: 0.5px solid ${COLOR_SYSTEM.get("Gray")[200]};
      border-radius: 12px;
      padding: 0 12px;
      margin: 0px 0;
      font-style: normal;
      
      /* 중첩된 blockquote 방지 */
      blockquote {
        background-color: transparent;
        border-left: none;
        padding-left: 0;
      }
    }
    
    code {
      padding: 2px 4px;
      border-radius: 3px;
      font-family: 'Consolas', 'Monaco', monospace;
      font-size: 0.9em;
      background-color: inherit; /* 부모 요소의 배경색을 상속 */
    }
    
    /* mark 안의 code는 mark의 배경색을 사용 */
    mark code {
      background-color: transparent;
    }
    
    pre {
      background: ${COLOR_SYSTEM.get('Gray')[100]};
      padding: 12px;
      border-radius: 4px;
      overflow-x: auto;
      margin: 8px 0;
      
      code {
        background: none;
        padding: 0;
        font-size: 0.9em;
      }
    }
    
    hr {
      border: none;
      border-top: 1px solid ${COLOR_SYSTEM.get('Gray')[300]};
      margin: 16px 0;
    }
    
    /* 이미지 스타일 */
    img.editor-image {
      max-width: 100%;
      height: auto;
      /* 약품 이미지는 inline으로 표시하여 약품명과 나란히 배치 */
      display: inline;
      margin: 0 8px;
      vertical-align: middle;
    }

    /* 모든 이미지가 컨테이너를 넘지 않도록 */
    img {
      max-width: 100%;
      height: auto;
    }
    
    strong { 
      font-weight: 700;
      font-size: inherit;
    }
    
    em { 
      font-style: italic;
      font-size: inherit;
    }
    
    s { 
      text-decoration: line-through;
      font-size: inherit;
    }
    
    /* 모든 inline 요소에 대한 기본 폰트 크기 */
    span, a, b, i, u, mark {
      font-size: inherit;
      line-height: inherit;
      letter-spacing: inherit;
    }

    /* DrugImage 카드 - ProseMirror span 스타일 간섭 방지 */
    [data-drug-container="true"] span {
      margin: 0;
    }
    [data-drug-name="true"] {
      font-size: 12px;
      line-height: 1.4;
    }
    [data-drug-chart="true"],
    [data-drug-print="true"] {
      font-size: 11px;
      line-height: 1.3;
    }

    /* 테이블 스타일 */
    table {
      width: auto;
      max-width: 100%;
      border-collapse: collapse;
      margin: 16px 0;
      border: 1px solid ${COLOR_SYSTEM.get("Gray")[300]};
      font-size: 12px;
      table-layout: auto;
    }

    thead {
    }

    th {
      padding: 2px;
      text-align: left;
      font-weight: 600;
      border: 1px solid ${COLOR_SYSTEM.get("Gray")[300]};
      vertical-align: top;
      min-width: 50px;
      background-color: ${COLOR_SYSTEM.get("Gray")[50]};
    }

    td {
      padding: 2px;
      border: 1px solid ${COLOR_SYSTEM.get("Gray")[300]};
      vertical-align: top;
      min-width: 50px;
    }

    /* 텍스트 선택 스타일 */
    ::selection {
      background: rgba(0, 123, 255, 0.2);
    }

    ::-moz-selection {
      background: rgba(0, 123, 255, 0.2);
    }
  }
`;


interface AIReviewHTMLEditorProps {
  initialContent: string;
  onChange: (content: string) => void;
  promptType?: string;
  onReferenceClick?: (referenceId: string) => void;
  onDelete?: () => void;
  hasEditedVersion?: boolean;
  isSaving?: boolean;
  drugsData?: Drug[];
  pillsData?: DrugSearchResult[];
}

export interface AIReviewHTMLEditorRef {
  insertText: (text: string) => void;
}


const AIReviewHTMLEditor = forwardRef<AIReviewHTMLEditorRef, AIReviewHTMLEditorProps>(({
  initialContent,
  onChange,
  promptType,
  onReferenceClick,
  onDelete,
  hasEditedVersion = false,
  isSaving = false,
  drugsData,
  pillsData
}, ref) => {
  const [showTextColorPicker, setShowTextColorPicker] = useState(false);
  const [showBgColorPicker, setShowBgColorPicker] = useState(false);
  const [showTableMenu, setShowTableMenu] = useState(false);
  const [selectedTextColor, setSelectedTextColor] = useState('#000000');
  const [selectedBgColor, setSelectedBgColor] = useState('#ffff00');
  const [textColorPosition, setTextColorPosition] = useState({ top: 0, left: 0 });
  const [bgColorPosition, setBgColorPosition] = useState({ top: 0, left: 0 });
  const [tableMenuPosition, setTableMenuPosition] = useState({ top: 0, left: 0 });
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [isInTable, setIsInTable] = useState(false);
  const [editorUpdateTrigger, setEditorUpdateTrigger] = useState(0);
  const textColorRef = useRef<HTMLDivElement>(null);
  const bgColorRef = useRef<HTMLDivElement>(null);
  const tableMenuRef = useRef<HTMLDivElement>(null);
  const toolbarRef = useRef<HTMLDivElement>(null);

  // TipTap 에디터 설정 - HTML WYSIWYG 모드
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3]
        },
        code: false // StarterKit의 code 비활성화
      }),
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      Code.configure({
        HTMLAttributes: {
          class: null,
        },
      }),
      Link.configure({
        openOnClick: false,
        linkOnPaste: true,
        autolink: true,
        protocols: ['http', 'https', 'mailto', 'tel'],
        HTMLAttributes: {
          target: null,
          rel: null,
          class: null,
        },
      }),
      DrugImage.configure({
        drugsData,
        pillsData,
      }),
      Image.configure({
        inline: true,  // 인라인으로 변경
        allowBase64: true,
        HTMLAttributes: {
          class: 'editor-image',
          style: 'width: 100px; object-fit: contain; margin: 0 8px; vertical-align: middle;',
        },
      }),
      Table.configure({
        resizable: false,
        HTMLAttributes: {
          class: 'tiptap-table',
        },
      }),
      TableRow,
      TableHeader,
      TableCell,
      Reference.configure({
        onReferenceClick,
      }),
      // 병원 노드 — span[data-hospital-alias] 파싱·직렬화를 스키마에 등록(없으면 저장 시 alias 소실).
      Hospital,
    ],
    content: initialContent,
    onUpdate: ({ editor }) => {
      // HTML 직접 가져오기
      const html = editor.getHTML();
      onChange(html);

      // 표 안팎 상태 업데이트
      const inTable = editor.isActive('table');
      setIsInTable(inTable);

      // 표 밖으로 나가면 표 메뉴 닫기
      if (!inTable) {
        setShowTableMenu(false);
      }
    },
    onSelectionUpdate: ({ editor }) => {
      // 커서 이동 시 표 안팎 상태 업데이트
      const inTable = editor.isActive('table');
      setIsInTable(inTable);

      // 표 밖으로 나가면 표 메뉴 닫기
      if (!inTable) {
        setShowTableMenu(false);
      }

      // 강제 리렌더링 트리거 (툴바 버튼 상태 업데이트를 위해)
      setEditorUpdateTrigger(prev => prev + 1);
    },
  });

  // TipTap 기반 스타일 버튼들
  const handleUndo = () => editor?.chain().focus().undo().run();
  const handleRedo = () => editor?.chain().focus().redo().run();
  const handleBold = () => editor?.chain().focus().toggleBold().run();
  const handleItalic = () => editor?.chain().focus().toggleItalic().run();
  const handleUnderline = () => editor?.chain().focus().toggleUnderline().run();
  const handleStrike = () => editor?.chain().focus().toggleStrike().run();
  const handleH1 = () => editor?.chain().focus().toggleHeading({ level: 1 }).run();
  const handleH2 = () => editor?.chain().focus().toggleHeading({ level: 2 }).run();
  const handleH3 = () => editor?.chain().focus().toggleHeading({ level: 3 }).run();
  const handleBulletList = () => editor?.chain().focus().toggleBulletList().run();
  const handleOrderedList = () => editor?.chain().focus().toggleOrderedList().run();
  const handleBlockquote = () => editor?.chain().focus().toggleBlockquote().run();
  const handleCode = () => editor?.chain().focus().toggleCode().run();
  const handleCodeBlock = () => editor?.chain().focus().toggleCodeBlock().run();
  const handleHorizontalRule = () => editor?.chain().focus().setHorizontalRule().run();

  // 표 삽입 함수
  const handleInsertTable = () => {
    if (!editor) return;
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  };

  // 이미지 삽입 함수
  const handleInsertImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    
    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;
      
      // 이미지 리사이즈 및 base64 변환
      const reader = new FileReader();
      reader.onload = async (event) => {
        const img = new window.Image();
        img.onload = () => {
          // Canvas를 사용하여 이미지 리사이즈
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) return;
          
          // 최대 너비 설정
          const maxWidth = 1920;
          let width = img.width;
          let height = img.height;
          
          // 비율 유지하며 리사이즈
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // 이미지 그리기
          ctx.drawImage(img, 0, 0, width, height);
          
          // base64로 변환
          const base64 = canvas.toDataURL('image/jpeg', 0.9);
          
          // 에디터에 삽입
          editor?.chain().focus().setImage({ src: base64 }).run();
        };
        
        img.src = event.target?.result as string;
      };
      
      reader.readAsDataURL(file);
    };
    
    input.click();
  };

  // 색상 적용 함수들
  const handleTextColor = (color: string) => {
    if (!editor) return;

    editor.chain()
      .focus()
      .setColor(color)
      .run();

    setSelectedTextColor(color);
  };

  const handleBgColor = (color: string) => {
    if (!editor) return;

    editor.chain()
      .focus()
      .toggleHighlight({ color })
      .run();

    setSelectedBgColor(color);
  };
  
  // 최근 색상으로 바로 적용
  const applyRecentTextColor = () => {
    editor?.chain()
      .focus()
      .setColor(selectedTextColor)
      .run();
  };
  
  const applyRecentBgColor = () => {
    editor?.chain()
      .focus()
      .toggleHighlight({ color: selectedBgColor })
      .run();
  };

  // 색상 팔레트
  const textColors = [
    '#000000', '#FF0000', '#00FF00', '#0000FF', '#FFFF00', '#FF00FF', '#00FFFF', '#808080',
    '#800000', '#008000', '#000080', '#808000', '#800080', '#008080', '#C0C0C0', '#FFFFFF'
  ];

  const bgColors = [
    '#FFFF00', '#00FF00', '#00FFFF', '#FF00FF', '#FFA500', '#FFC0CB', '#E6E6FA', '#98FB98',
    '#FFE4B5', '#FFB6C1', '#E0FFFF', '#D3D3D3', '#90EE90', '#FFD700', '#F0E68C', '#FFFFFF'
  ];

  // 문서 타입별 색상 및 스타일 정의
  const getButtonStyleByType = (docType: DocumentType) => {
    switch (docType) {
      case "BEERS2023":
        return {
          color: COLOR_SYSTEM.get("Orange")?.[500],
        };
      case "SPSTv3":
      case "SPSTv2":
        return {
          color: COLOR_SYSTEM.get("Green")?.[500],
        };
      case "CCMM":
      case "LCMM":
        return {
          color: COLOR_SYSTEM.get("Purple")?.[500],
        };
      case "POLY":
        return {
          color: COLOR_SYSTEM.get("Purple")?.[500],
        };
      case "LABEL":
      default:
        return {
          color: COLOR_SYSTEM.get("Blue")?.[500],
        };
    }
  };
  
  // HTML을 파싱하여 참고문헌 링크를 버튼으로 변환

  // 색상 팔레트 토글
  const toggleTextColorPicker = () => {
    if (!showTextColorPicker && textColorRef.current) {
      const rect = textColorRef.current.getBoundingClientRect();
      setTextColorPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
    setShowTextColorPicker(!showTextColorPicker);
  };

  const toggleBgColorPicker = () => {
    if (!showBgColorPicker && bgColorRef.current) {
      const rect = bgColorRef.current.getBoundingClientRect();
      setBgColorPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
    setShowBgColorPicker(!showBgColorPicker);
  };

  // 표 메뉴 토글
  const toggleTableMenu = () => {
    if (!showTableMenu && tableMenuRef.current) {
      const rect = tableMenuRef.current.getBoundingClientRect();
      setTableMenuPosition({
        top: rect.bottom + 4,
        left: rect.left
      });
    }
    setShowTableMenu(!showTableMenu);
  };

  // 표 메뉴 아이템 클릭 핸들러
  const handleAddRowBefore = () => {
    editor?.chain().focus().addRowBefore().run();
  };

  const handleAddRowAfter = () => {
    editor?.chain().focus().addRowAfter().run();
  };

  const handleDeleteRow = () => {
    editor?.chain().focus().deleteRow().run();
  };

  const handleAddColumnBefore = () => {
    editor?.chain().focus().addColumnBefore().run();
  };

  const handleAddColumnAfter = () => {
    editor?.chain().focus().addColumnAfter().run();
  };

  const handleDeleteColumn = () => {
    editor?.chain().focus().deleteColumn().run();
  };

  const handleDeleteTable = () => {
    editor?.chain().focus().deleteTable().run();
    setShowTableMenu(false);
  };

  // 스크롤 가능 여부 체크 함수
  const checkScrollable = () => {
    if (!toolbarRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = toolbarRef.current;
    setCanScrollLeft(scrollLeft > 0);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - 1);
  };

  // 스크롤 함수
  const scrollToolbar = (direction: 'left' | 'right') => {
    if (!toolbarRef.current) return;
    
    const scrollAmount = 200;
    const currentScroll = toolbarRef.current.scrollLeft;
    const newScroll = direction === 'left' 
      ? Math.max(0, currentScroll - scrollAmount)
      : currentScroll + scrollAmount;
    
    toolbarRef.current.scrollTo({
      left: newScroll,
      behavior: 'smooth'
    });
  };

  // 툴바 스크롤 상태 감지
  useEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;

    // 초기 체크
    checkScrollable();
    
    // 스크롤 이벤트 리스너
    const handleScroll = () => {
      checkScrollable();
    };
    
    // 리사이즈 이벤트 리스너
    const handleResize = () => {
      checkScrollable();
    };
    
    toolbar.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleResize);
    
    // 약간의 딜레이 후 다시 체크 (컨텐츠 로드 대응)
    const timeoutId = setTimeout(checkScrollable, 100);
    
    return () => {
      toolbar.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
    };
  }, [editor]);


  // 외부에서 텍스트 삽입을 위한 메서드 노출
  useImperativeHandle(ref, () => ({
    insertText: (text: string) => {
      if (!editor) return;
      
      // 참고문헌 span인지 확인
      const referenceMatch = text.match(/<span data-reference-id="([^"]+)" data-reference-link="true">([^<]+)<\/span>/);
      if (referenceMatch) {
        const referenceId = referenceMatch[1];
        const referenceText = referenceMatch[2];
        
        // Reference 노드로 삽입
        editor.chain()
          .focus()
          .command(({ commands }) => {
            return commands.setReference({
              referenceId,
              text: referenceText,
            });
          })
          .run();
      } else {
        // 일반 텍스트로 삽입
        editor.chain().focus().insertContent(text).run();
      }
    }
  }));

  if (!editor) {
    return null;
  }

  return (
    <EditorWrapper>
      <ToolbarArea>
          <EditorToolbar>
          <ScrollIndicatorLeft visible={canScrollLeft} />
          <ScrollIndicatorRight visible={canScrollRight} />
          <ScrollArrowLeft 
            visible={canScrollLeft} 
            onClick={() => scrollToolbar('left')}
            title="왼쪽으로 스크롤"
          >
            <Icon name="Chevron Left" size={16} color={COLOR_SYSTEM.get('Gray')[600]} />
          </ScrollArrowLeft>
          <ScrollArrowRight 
            visible={canScrollRight} 
            onClick={() => scrollToolbar('right')}
            title="오른쪽으로 스크롤"
          >
            <Icon name="Chevron Right" size={16} color={COLOR_SYSTEM.get('Gray')[600]} />
          </ScrollArrowRight>
          <ToolbarLeft ref={toolbarRef}>
          <ToolbarGroup>
            <ToolbarButton
              onClick={handleUndo}
              title="되돌리기 (Ctrl+Z)"
              disabled={!editor?.can().undo()}
            >
              <Icon name="Arrow Left" size={16} color={COLOR_SYSTEM.get('Gray')[700]} />
            </ToolbarButton>
            <ToolbarButton
              onClick={handleRedo}
              title="다시실행 (Ctrl+Shift+Z)"
              disabled={!editor?.can().redo()}
            >
              <Icon name="Arrow Right" size={16} color={COLOR_SYSTEM.get('Gray')[700]} />
            </ToolbarButton>
          </ToolbarGroup>

          <ToolbarDivider />

          <ToolbarGroup>
            <ColorDropdown ref={textColorRef}>
              <ToolbarButton
                onClick={applyRecentTextColor}
                title="텍스트 색상 적용"
                colorPreview={selectedTextColor}
                isColorButton
              >
                A
              </ToolbarButton>
              <ColorPickerButton
                onClick={toggleTextColorPicker}
                title="텍스트 색상 선택"
              />
            </ColorDropdown>

            <ColorDropdown ref={bgColorRef}>
              <ToolbarButton
                onClick={applyRecentBgColor}
                title="배경 색상 적용"
                colorPreview={selectedBgColor}
                isColorButton
              >
                <span style={{ backgroundColor: selectedBgColor, padding: '0 2px' }}>A</span>
              </ToolbarButton>
              <ColorPickerButton
                onClick={toggleBgColorPicker}
                title="배경 색상 선택"
              />
            </ColorDropdown>
          </ToolbarGroup>

          <ToolbarDivider />
          
          <ToolbarGroup>
            <ToolbarButton 
              onClick={handleBold} 
              title="굵게 (Ctrl+B)"
              isActive={editor?.isActive('bold')}
            >
              <strong>B</strong>
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleItalic} 
              title="기울임 (Ctrl+I)"
              isActive={editor?.isActive('italic')}
            >
              <em>I</em>
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleUnderline} 
              title="밑줄 (Ctrl+U)"
              isActive={editor?.isActive('underline')}
            >
              <u>U</u>
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleStrike} 
              title="취소선"
              isActive={editor?.isActive('strike')}
            >
              <s>S</s>
            </ToolbarButton>
          </ToolbarGroup>
          
          <ToolbarDivider />
          
          <ToolbarGroup>
            <ToolbarButton 
              onClick={handleH1} 
              title="제목 1"
              isActive={editor?.isActive('heading', { level: 1 })}
            >
              H1
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleH2} 
              title="제목 2"
              isActive={editor?.isActive('heading', { level: 2 })}
            >
              H2
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleH3} 
              title="제목 3"
              isActive={editor?.isActive('heading', { level: 3 })}
            >
              H3
            </ToolbarButton>
          </ToolbarGroup>
          
          <ToolbarDivider />
          
          <ToolbarGroup>
            <ToolbarButton 
              onClick={handleBulletList} 
              title="글머리 기호"
              isActive={editor?.isActive('bulletList')}
            >
              •
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleOrderedList} 
              title="번호 목록"
              isActive={editor?.isActive('orderedList')}
            >
              1.
            </ToolbarButton>
            <ToolbarButton 
              onClick={handleBlockquote} 
              title="인용"
              isActive={editor?.isActive('blockquote')}
            >
              ❝
            </ToolbarButton>
          </ToolbarGroup>
          
          <ToolbarDivider />

          <ToolbarGroup>
            <TableDropdown ref={tableMenuRef}>
              <ToolbarButton
                onClick={handleInsertTable}
                title="표 삽입 (3x3)"
                isActive={isInTable}
              >
                표
              </ToolbarButton>
              <ColorPickerButton
                onClick={toggleTableMenu}
                title="표 편집 메뉴"
                disabled={!isInTable}
              />
            </TableDropdown>
            <ToolbarButton
              onClick={handleInsertImage}
              title="이미지 삽입"
            >
              <Icon name="Image" size={16} color={COLOR_SYSTEM.get('Gray')[700]} />
            </ToolbarButton>
          </ToolbarGroup>

          {hasEditedVersion && (
            <>
              <ToolbarDivider />
              <ToolbarGroup>
                <ToolbarButton 
                  onClick={onDelete} 
                  title="수정본 삭제"
                  disabled={isSaving}
                >
                  <Icon name="Trash" size={16} color={COLOR_SYSTEM.get('Red')[600]} />
                </ToolbarButton>
              </ToolbarGroup>
            </>
          )}
        </ToolbarLeft>
        </EditorToolbar>
      </ToolbarArea>
      <EditArea>
        <EditorContentWrapper>
          <EditorContent editor={editor} />
        </EditorContentWrapper>
      </EditArea>
      {showTextColorPicker && (
        <ColorGrid 
          data-color-grid="text"
          style={{ top: textColorPosition.top, left: textColorPosition.left }}
        >
          {textColors.map(color => (
            <ColorButton
              key={color}
              color={color}
              onClick={() => handleTextColor(color)}
              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
              title={color}
            />
          ))}
        </ColorGrid>
      )}
      {showBgColorPicker && (
        <ColorGrid
          data-color-grid="bg"
          style={{ top: bgColorPosition.top, left: bgColorPosition.left }}
        >
          {bgColors.map(color => (
            <ColorButton
              key={color}
              color={color}
              onClick={() => handleBgColor(color)}
              onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
              title={color}
            />
          ))}
        </ColorGrid>
      )}
      {showTableMenu && (
        <TableMenuGrid style={{ top: tableMenuPosition.top, left: tableMenuPosition.left }}>
          <TableMenuItem
            onClick={handleAddRowBefore}
            disabled={!editor?.can().addRowBefore()}
          >
            위에 행 추가
          </TableMenuItem>
          <TableMenuItem
            onClick={handleAddRowAfter}
            disabled={!editor?.can().addRowAfter()}
          >
            아래에 행 추가
          </TableMenuItem>
          <TableMenuItem
            onClick={handleDeleteRow}
            disabled={!editor?.can().deleteRow()}
          >
            행삭제
          </TableMenuItem>
          <TableMenuItem
            onClick={handleAddColumnBefore}
            disabled={!editor?.can().addColumnBefore()}
          >
            왼쪽에 열 추가
          </TableMenuItem>
          <TableMenuItem
            onClick={handleAddColumnAfter}
            disabled={!editor?.can().addColumnAfter()}
          >
            오른쪽에 열 추가
          </TableMenuItem>
          <TableMenuItem
            onClick={handleDeleteColumn}
            disabled={!editor?.can().deleteColumn()}
          >
            열삭제
          </TableMenuItem>
          <TableMenuItem
            onClick={handleDeleteTable}
            disabled={!editor?.can().deleteTable()}
          >
            표삭제
          </TableMenuItem>
        </TableMenuGrid>
      )}
    </EditorWrapper>
  );
});

AIReviewHTMLEditor.displayName = 'AIReviewHTMLEditor';

export default AIReviewHTMLEditor;